import { cn } from '@bridebook/aura';
import { ImageX } from '@bridebook/ui';
import {
  GLAMOUR_ACCENTS,
  Palette,
  PALETTE_ACCENTS,
  SKETCHES_ACCENTS,
} from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/palette-config';
import { useThemeContext } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/theme-provider';

const getExtraTopSpacingClassName = (palette: Palette) => {
  switch (palette) {
    case Palette.Timeless:
      return 'h-[10px]';
    case Palette.Blossom:
      return 'h-[80px]';
    default:
      return '';
  }
};

/**
 * Mobile accent component that renders decorative elements positioned relative to the wedding overview section.
 * These accents are only visible on mobile devices (@screen-lg:hidden).
 * Different accent styles are rendered based on the selected palette theme.
 */

export const MobileAccent = () => {
  const { palette } = useThemeContext();

  return (
    <>
      <div
        className={cn(
          'pointer-events-none block @screen-lg:hidden',
          getExtraTopSpacingClassName(palette),
        )}
      />
      <div className="absolute top-0 bottom-0 left-1/2 w-full max-w-[587px] -translate-x-1/2 overflow-hidden">
        <Accent />
      </div>
    </>
  );
};

const Accent = () => {
  const { palette } = useThemeContext();

  switch (palette) {
    case Palette.Bridebook:
    case Palette.BridebookGreen:
    case Palette.BridebookBlush:
      return (
        <MobileBridebookSwirl className="absolute right-0 bottom-0 left-0 h-auto w-full text-[var(--ww-palette-accent)] @screen-lg:hidden" />
      );
    case Palette.Roses:
    case Palette.RosesPlum:
    case Palette.RosesBrown:
      return (
        <>
          <div className="absolute top-0 left-0 h-[114px] w-[69px] @screen-lg:hidden">
            <ImageX w={69} h={114} src={PALETTE_ACCENTS[palette]} fit="cover" />
          </div>
          <div className="absolute right-0 bottom-0 h-[114px] w-[69px] scale-x-[-1] scale-y-[-1] @screen-lg:hidden">
            <ImageX w={69} h={114} src={PALETTE_ACCENTS[palette]} fit="cover" />
          </div>
        </>
      );
    case Palette.Foliage:
    case Palette.FoliageOrange:
    case Palette.FoliageBlue:
      return (
        <>
          <div className="absolute bottom-[130px] left-[12px] h-[107px] w-[55px] @screen-lg:hidden">
            <ImageX w={55} h={107} src={PALETTE_ACCENTS[palette]} fit="cover" />
          </div>
          <div className="absolute right-[12px] bottom-[130px] h-[107px] w-[55px] scale-x-[-1] @screen-lg:hidden">
            <ImageX w={55} h={107} src={PALETTE_ACCENTS[palette]} fit="cover" />
          </div>
        </>
      );
    case Palette.Whimsical:
    case Palette.WhimsicalBeige:
    case Palette.WhimsicalGreen:
      return (
        <>
          <div className="absolute bottom-[121px] left-[4px] h-[131px] w-[91px] @screen-lg:hidden">
            <ImageX w={91} h={131} src={PALETTE_ACCENTS[palette]} fit="cover" />
          </div>
          <div className="absolute right-[4px] bottom-[121px] h-[131px] w-[91px] scale-x-[-1] @screen-lg:hidden">
            <ImageX w={91} h={131} src={PALETTE_ACCENTS[palette]} fit="cover" />
          </div>
        </>
      );
    case Palette.Sketches:
    case Palette.SketchesGreen:
    case Palette.SketchesLilac:
      return (
        <>
          <div className="absolute top-0 left-0 h-[259px] w-[81px] @screen-lg:hidden">
            <ImageX w={81} h={259} src={SKETCHES_ACCENTS[palette].mobileLeft} fit="cover" />
          </div>
          <div className="absolute top-0 right-0 h-[243px] w-[152px] @screen-lg:hidden">
            <ImageX w={152} h={243} src={SKETCHES_ACCENTS[palette].mobileRight} fit="cover" />
          </div>
        </>
      );
    case Palette.Glamour:
    case Palette.GlamourNavy:
    case Palette.GlamourChampagne:
      return (
        <>
          <div className="absolute top-0 left-0 h-[150px] w-[150px] @screen-lg:hidden">
            <ImageX w={150} h={150} src={GLAMOUR_ACCENTS.mobile} fit="cover" />
          </div>
          <div className="absolute top-0 right-0 h-[150px] w-[150px] scale-x-[-1] @screen-lg:hidden">
            <ImageX w={150} h={150} src={GLAMOUR_ACCENTS.mobile} fit="cover" />
          </div>
        </>
      );
    case Palette.Timeless:
      return (
        <div className="absolute top-[12px] left-1/2 h-[32px] w-[200px] -translate-x-1/2 @screen-lg:hidden">
          <ImageX w={200} h={32} src={PALETTE_ACCENTS[palette]} fit="cover" />
        </div>
      );

    case Palette.Blossom:
      return (
        <div className="absolute top-[20px] left-1/2 h-[87px] w-[65px] -translate-x-1/2 @screen-lg:hidden">
          <ImageX w={65} h={87} src={PALETTE_ACCENTS[palette]} fit="cover" />
        </div>
      );
    case Palette.Amore:
      return (
        <>
          <div className="absolute top-[22px] left-[8px] h-[120px] w-[120px] @screen-lg:hidden">
            <ImageX w={120} h={120} src={PALETTE_ACCENTS[palette]} fit="cover" />
          </div>
          <div className="absolute top-[22px] right-[8px] h-[120px] w-[120px] scale-x-[-1] @screen-lg:hidden">
            <ImageX w={120} h={120} src={PALETTE_ACCENTS[palette]} fit="cover" />
          </div>
        </>
      );

    default:
      return null;
  }
};

const MobileBridebookSwirl = ({ className }: { className?: string }) => (
  <svg
    width={375}
    height={926}
    viewBox="0 0 375 926"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}>
    <path
      d="M116.985 248.998C121.408 248.487 128.016 248.844 134.175 253.253C140.341 257.668 142.859 263.855 143.827 268.392C144.766 272.789 144.605 277.079 144.209 280.565C143.411 287.584 141.067 296.018 137.747 305.557C130.998 324.945 118.55 353.493 99.1572 393.767C83.82 425.618 73.5953 449.491 67.458 466.869C66.1198 470.658 64.9994 474.073 64.0742 477.138C65.136 476.414 66.2515 475.641 67.418 474.812C88.0925 460.119 115.485 435.852 144.757 410.559C172.745 386.375 202.591 361.132 227.03 346.03L228.19 345.318L230.046 344.188L232.101 343.482C249.857 337.389 266.729 336.38 281.756 342.564C296.778 348.747 306.443 360.674 312.759 373.205C324.716 396.93 328.222 430.475 331.185 457.322C332.762 471.615 334.182 484.575 336.267 495.802C338.412 507.359 340.891 514.802 343.45 518.983C344.418 520.564 345.089 521.152 345.288 521.306C345.357 521.314 345.466 521.323 345.622 521.324C347.655 521.337 354.171 520.051 367.081 510.785C369.667 508.929 372.311 507.186 375 505.599V552.963C365.084 558.216 355.181 561.383 345.377 561.323C328.254 561.218 316.478 551.535 309.336 539.868C302.799 529.191 299.286 515.749 296.938 503.103C294.529 490.125 292.955 475.559 291.427 461.71C288.178 432.273 285.032 407.067 277.039 391.208C273.38 383.948 269.744 380.875 266.533 379.554C263.511 378.31 257.743 377.337 247.082 380.664C226.068 393.824 199.088 416.476 170.909 440.825C142.707 465.194 113.269 491.299 90.5898 507.417C79.7991 515.086 67.7789 522.574 56.8604 525.104C51.2498 526.405 42.2246 527.27 33.7217 521.964C24.6813 516.322 21.6128 507.166 20.7598 500.329C19.9192 493.592 20.766 486.294 22.2568 479.153C23.7996 471.764 26.3218 463.232 29.7412 453.55C36.58 434.185 47.5123 408.822 63.1182 376.412C76.1037 349.444 85.6064 328.468 92.249 312.417C88.3886 315.733 84.3168 319.248 80.0449 322.905C67.0982 333.99 52.6291 346.085 38.7734 355.519C31.8219 360.252 24.6027 364.609 17.4443 367.889C12.1508 370.314 6.22387 372.461 0 373.488V331.872C0.254044 331.76 0.515903 331.646 0.783203 331.523C5.18315 329.508 10.3702 326.466 16.2617 322.455C28.0928 314.4 41.0627 303.624 54.0293 292.521C66.538 281.811 79.4973 270.388 89.9512 262.515C95.1104 258.629 100.775 254.736 106.246 252.195C108.966 250.932 112.699 249.494 116.985 248.998Z"
      fill="currentColor"
    />
    <path
      d="M375 720.151C369.28 732.822 363.32 746.337 357.17 760.114C342.092 793.891 325.781 829.415 310.062 853.462L309.313 854.598L308.113 856.408L306.552 857.92C293.06 870.973 278.211 879.048 261.98 879.826C245.755 880.604 231.942 873.907 220.904 865.241C200.008 848.835 182.597 819.948 168.52 796.896C161.025 784.624 154.238 773.492 147.586 764.211C140.738 754.656 135.334 748.969 131.242 746.27C129.696 745.25 128.839 745.002 128.594 744.947C128.528 744.969 128.425 745.007 128.283 745.072C126.437 745.924 121.082 749.853 113.325 763.723C104.182 780.072 91.5973 793.684 73.8203 796.04C56.3097 798.36 42.0701 788.591 32.4971 779.513C22.4314 769.967 13.2083 757.108 5.05176 744.442C3.35902 741.814 1.67377 739.128 0 736.417V659.704C5.41371 667.483 10.8159 676.381 16.0117 685.139C23.6069 697.94 30.9929 710.847 38.6807 722.784C46.4284 734.815 53.5563 744.357 60.0215 750.488C65.2167 755.415 67.9113 756.207 68.5742 756.341C69.4468 755.945 73.0283 753.828 78.4141 744.198C87.4487 728.044 98.0153 714.983 111.528 708.75C127.077 701.578 141.85 705.347 153.269 712.88C163.719 719.774 172.604 730.454 180.098 740.908C187.787 751.636 195.395 764.157 202.657 776.049C218.092 801.324 231.638 822.812 245.606 833.779C252.001 838.8 256.598 840.039 260.065 839.873C263.33 839.716 268.965 838.15 277.206 830.612C290.648 809.777 305.464 777.816 320.645 743.809C335.838 709.774 351.413 673.642 365.107 649.423C368.149 644.044 371.449 638.588 375 633.594V720.151Z"
      fill="currentColor"
    />
    <path
      d="M203.312 0C206.426 11.6161 209.05 24.3506 211.58 36.5205C217.608 65.5157 223.133 90.3098 232.596 105.338C236.928 112.217 240.839 114.931 244.16 115.941C247.287 116.893 253.121 117.314 263.418 112.989C283.088 97.8932 307.795 72.7825 333.535 45.8682C347.404 31.367 361.582 16.3311 375 2.69922V60.3564C370.87 64.689 366.679 69.0872 362.443 73.5156C336.878 100.247 309.562 128.21 286.667 145.563L285.58 146.382L283.841 147.683L281.861 148.58C264.763 156.331 248.064 158.938 232.518 154.209C216.976 149.481 206.223 138.525 198.746 126.65C184.59 104.168 177.916 71.1071 172.418 44.6621C169.491 30.5837 166.846 17.8175 163.705 6.83887C162.997 4.36405 162.288 2.08812 161.588 0H203.312ZM125.251 0C112.651 9.41842 98.4915 15.1985 83.5303 11.9863C74.751 10.1013 67.8205 5.55742 62.3438 0H125.251Z"
      fill="currentColor"
    />
  </svg>
);
